import { useEffect, useRef, useState } from "react"
import { ensureGeoFlowSession } from "../services/geoflowApi"
import { checkAndRefreshToken } from "@/services/auth"
import { useAccessContext } from "../contexts/AccessContext"

// GeoFlow-Session lebt 12h (Max-Age 43200) — deutlich vorher erneuern, sonst
// laufen Kachel-Requests in einem über Nacht offenen Tab in stumme 401s
const RENEW_AFTER_MS = 6 * 3600 * 1000
const RENEW_CHECK_MS = 15 * 60 * 1000

export function useGeoFlowSession() {
  const { accessContext, setAccessContext } = useAccessContext()
  const [geoflowBase, setGeoflowBase] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const negotiatedAtRef = useRef(0)

  useEffect(() => {
    // Ohne Token keinen aussichtslosen Handshake feuern; Effekt läuft bei Token-Ankunft erneut
    if (!accessContext.token) {
      return undefined
    }

    let cancelled = false

    async function run() {
      // Frische Session nicht doppelt aushandeln (Effekt feuert bei jedem
      // Token-Refresh, der Intervall-Check zusätzlich)
      if (Date.now() - negotiatedAtRef.current < RENEW_AFTER_MS) {
        return
      }
      const initial = negotiatedAtRef.current === 0
      try {
        if (initial) {
          setLoading(true)
          setError(null)
        }

        const accessToken = await checkAndRefreshToken(accessContext, setAccessContext)
        if (!accessToken) {
          throw new Error("No valid access token for GeoFlow handshake")
        }
        const base = await ensureGeoFlowSession(accessToken)

        // Ref nur zusammen mit dem State setzen: ein abgebrochener Lauf darf
        // den Retry des Nachfolgers nicht blockieren, sonst bleibt base null
        if (!cancelled) {
          negotiatedAtRef.current = Date.now()
          setGeoflowBase(base)
        }
      } catch (err) {
        if (!cancelled && initial) {
          // Fehler nur beim Erst-Handshake in die UI heben — bei einer
          // fehlgeschlagenen Erneuerung trägt die alte Session noch bis zu 6h,
          // der Intervall-Check probiert es weiter
          setError(
            err instanceof Error ? err.message : "Unknown GeoFlow session error",
          )
        }
      } finally {
        if (!cancelled && initial) {
          setLoading(false)
        }
      }
    }

    run()
    const intervalId = setInterval(run, RENEW_CHECK_MS)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [accessContext.token])

  // GeoFlows Pfad-Layout an einer Stelle für beide Consumer (Haupt-Map + Preview)
  return {
    geoflowBase,
    geoserverUrl: geoflowBase ? `${geoflowBase}/geoserver` : undefined,
    geoflowApiUrl: geoflowBase ? `${geoflowBase}/api` : undefined,
    loading,
    error,
  }
}
